import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import type { AppConfig, ChartConfigItem, CosmosDBHealth } from "../types/AppTypes";
import { generateUUIDv4 } from "../configs/Utils";
import { getUserInfo as getUserInfoApi, type UserInfo } from "../api/api";

export interface AppState {
  selectedConversationId: string;
  generatedConversationId: string;
  config: {
    appConfig: AppConfig;
    charts: ChartConfigItem[];
  };
  cosmosInfo: CosmosDBHealth;
  showAppSpinner: boolean;
  userInfo: UserInfo[];
  loadingUserInfo: boolean;
  userId: string;
  userName: string;
  userEmail: string;
  isAuthenticated: boolean;
}

const initialState: AppState = {
  selectedConversationId: "",
  generatedConversationId: generateUUIDv4(),
  config: {
    appConfig: null,
    charts: [],
  },
  cosmosInfo: { cosmosDB: false, status: "" },
  showAppSpinner: false,
  userInfo: [],
  loadingUserInfo: false,
  userId: "",
  userName: "",
  userEmail: "",
  isAuthenticated: false,
};

// Async thunks
export const fetchUserInfo = createAsyncThunk(
  'app/fetchUserInfo',
  async () => {
    const userInfoList = await getUserInfoApi();
    return userInfoList;
  }
);

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setSelectedConversationId: (state, action: PayloadAction<string>) => {
      state.selectedConversationId = action.payload;
    },
    generateNewConversationId: (state) => {
      state.generatedConversationId = generateUUIDv4();
    },
    startNewConversation: (state) => {
      state.selectedConversationId = "";
      state.generatedConversationId = generateUUIDv4();
    },
    saveConfig: (state, action: PayloadAction<AppState["config"]>) => {
      state.config = { ...state.config, ...action.payload };
    },
    setCosmosInfo: (state, action: PayloadAction<CosmosDBHealth>) => {
      state.cosmosInfo = action.payload;
    },
    setAppSpinner: (state, action: PayloadAction<boolean>) => {
      state.showAppSpinner = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        state.loadingUserInfo = true;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.userInfo = action.payload;
        state.loadingUserInfo = false;

        const userClaims = action.payload?.[0]?.user_claims || [];
        const nameClaim = userClaims.find((c: any) => c.typ === "name");
        const objectIdClaim = userClaims.find(
          (c: any) =>
            c.typ === "http://schemas.microsoft.com/identity/claims/objectidentifier"
        );
        const emailClaim = userClaims.find(
          (c: any) =>
            c.typ === "preferred_username" ||
            c.typ === "email" ||
            c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress" ||
            c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/upn"
        );

        state.userId = objectIdClaim?.val || action.payload?.[0]?.user_id || "";
        state.userName = nameClaim?.val || "";
        state.userEmail = emailClaim?.val || "";
        state.isAuthenticated = Boolean(action.payload?.[0]);
      })
      .addCase(fetchUserInfo.rejected, (state) => {
        state.loadingUserInfo = false;
        state.userId = "";
        state.userName = "";
        state.userEmail = "";
        state.isAuthenticated = false;
      });
  },
});

export const {
  setSelectedConversationId,
  generateNewConversationId,
  startNewConversation,
  saveConfig,
  setCosmosInfo,
  setAppSpinner,
} = appSlice.actions;

export default appSlice.reducer;
